import { cloneDeep } from 'lodash';
import React from 'react';
import { V } from '../common/Layout';
import FormModalInfoAlert from '../common/form/FormModalInfoAlert';
import InlineFieldsEditor from '../common/form/widgets/InlineFieldsEditor';
import ContactInlineEditorFormItems from './ContactInlineEditorFormItems';
import { useUpdatePersonBasicInfo } from './detailsPage/detailsPageUtilities/personBasicInformation';
import { QuickAddPersonOption } from './helpers/QuickAddPerson';
import { EMPTY_EMAIL, getNameInputFromString, personContactInfoValidationRules, updateName } from './person';

type ContactInlineEditorContact = Omit<QuickAddPersonOption, 'id' | 'ref'> & {
    id?: QuickAddPersonOption['id'];
    ref?: QuickAddPersonOption['ref'];
    personReference?: { id: string };
};

type ContactInlineEditorProps = {
    contact: ContactInlineEditorContact;
    onSaveCompleted: (state: QuickAddPersonOption) => void;
    onCancel: () => void;
};

export default function ContactInlineEditor({ contact, onSaveCompleted, onCancel }: ContactInlineEditorProps) {
    const { updatePersonInfo, basicInfoSaving } = useUpdatePersonBasicInfo();
    const { id, name, contactInfo, ref, personReference } = contact;
    const { firstName, lastName } =
        typeof name === 'string'
            ? getNameInputFromString(name)
            : updateName({ firstName: name?.firstName ?? '', lastName: name?.lastName ?? '' });

    const contactState = {
        identity: { id: id || ref?.id || personReference?.id },
        name: { firstName, lastName },
        contactInfo: {
            phone: contactInfo?.phone ?? null,
            ...cloneDeep(EMPTY_EMAIL)
        }
    };

    const handleSave = (contactState: QuickAddPersonOption) => {
        updatePersonInfo({ input: contactState }).then((id) => {
            onSaveCompleted(contactState);
        });
    };

    return (
        <V gap={1} sx={{ p: 0 }}>
            <FormModalInfoAlert descriptionToken={'person.email.required.info.message'} sx={{ p: 0 }} />
            <InlineFieldsEditor
                titleToken={'contact.edit'}
                titleTokenValues={{ firstName, lastName }}
                initialState={contactState}
                validationRules={personContactInfoValidationRules}
                inputFields={<ContactInlineEditorFormItems />}
                onSave={handleSave}
                onCancel={onCancel}
                isSaving={basicInfoSaving}
            />
        </V>
    );
}
